import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";

type SetAdrDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onApply: (adr: number, applyTo: "all" | "low" | "high") => void;
  currentAdr?: number;
};
type ApplyTo = "all" | "low" | "high";

export default function SetAdrDialog({
  isOpen,
  onClose,
  onApply,
  currentAdr = 140,
}: SetAdrDialogProps) {
  const [adr, setAdr] = useState(currentAdr);
  const [applyTo, setApplyTo] = useState<ApplyTo>("all");
  const [errors, setErrors] = useState("");

  useEffect(() => {
    if (isOpen) {
      setAdr(currentAdr);
      setApplyTo("all");
    }
  }, [isOpen, currentAdr]);

  const handleApply = () => {
    // Validation
    if (!adr || adr <= 0) {
      setErrors("ADR must be greater than 0");
      return;
    }
    if (adr > 10000) {
      setErrors("ADR seems unusually high (max €10,000)");
      return;
    }

    setErrors("");
    onApply(adr, applyTo);
    onClose();
  };

  const handleClose = () => {
    setErrors("");
    onClose();
  };

  const radioOptions = [
    {
      value: "all",
      label: "All months",
      description: "Set the same ADR for every month (default)",
    },
    {
      value: "low",
      label: "Low months only",
      description: "Occupancy ≤ 60% (typically off-season)",
    },
    {
      value: "high",
      label: "High months only",
      description: "Occupancy ≥ 80% (typically peak season)",
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg p-0">
        <DialogHeader>
          <DialogTitle>Set Base ADR</DialogTitle>
        </DialogHeader>

        <div className="space-y-8 p-6">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              ADR (Average Daily Rate)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 font-medium text-lg">
                €
              </span>
              <input
                type="number"
                value={adr || ""}
                onChange={(e) => setAdr(Number(e.target.value) || 0)}
                className={`w-full pl-8 pr-3 py-2.5 border rounded-lg text-lg transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors
                    ? "border-red-500"
                    : "border-slate-300 focus:border-blue-500"
                }`}
                placeholder="140"
                min="0"
                step="0.5"
                aria-invalid={!!errors}
              />
            </div>
            {errors && (
              <p className="mt-2 text-sm text-red-600 font-medium">{errors}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-4">
              Apply Rate To
            </label>
            <div className="space-y-3">
              {radioOptions.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-start p-3 border rounded-lg cursor-pointer transition-all duration-150 group 
                    ${
                      applyTo === option.value
                        ? "border-blue-500 bg-blue-50 shadow-md"
                        : "border-slate-200 hover:border-blue-300"
                    }`}
                >
                  <input
                    type="radio"
                    name="applyTo"
                    value={option.value}
                    checked={applyTo === option.value}
                    onChange={(e) => setApplyTo(e.target.value as ApplyTo)}
                    className="mt-1 mr-3 h-5 w-5 text-blue-600 border-slate-300 focus:ring-blue-500 transition-colors"
                  />
                  <div>
                    <span className="text-base font-medium text-slate-900">
                      {option.label}
                    </span>
                    <p className="text-sm text-slate-500 mt-0.5">
                      {option.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            className="bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg"
          >
            Apply ADR
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
